import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import DocumentPreview from 'components/modules/shipments/stops/DocumentPreview';
import { FileText, CloudUpload } from 'lucide-react';
import PropTypes from 'prop-types';
import { acceptToObject } from 'components/v2/Dropzone';
import { getFilesWithContentType } from 'utils/files/files';

export const FileUploader = forwardRef(
  (
    {
      onChange,
      fileTypes = ACCEPTED_FILE_TYPES,
      fileTypesLabel,
      multiple = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);

    useImperativeHandle(ref, () => {
      return {
        clearFiles: () => setFiles([]),
      };
    }, []);

    const onDrop = (newFiles) => {
      setFiles(newFiles);
      onChange?.(newFiles);
    };

    const removeFile = (id) => {
      const newFiles = files.filter((file) => file.preview !== id);

      setFiles(newFiles);
      onChange?.(newFiles);
    };

    const acceptObject = useMemo(() => acceptToObject(fileTypes), [fileTypes]);

    const { getRootProps, getInputProps } = useDropzone({
      onDropAccepted: (accepted) => onDrop(getFilesWithContentType(accepted)),
      multiple,
      accept: acceptObject,
    });

    const renderPreview = () => {
      const filePreviews = files.map((file, index) => {
        return (
          <div className="flex items-center gap-1" key={index}>
            <FileText className="h-4 w-4" />
            <DocumentPreview
              url={file.preview}
              type={file.type}
              name={file.name}
              useFileName
              handleRemove={(id) => removeFile(id)}
              removeIconStyles={{
                color: 'red',
                position: 'block',
                cursor: 'pointer',
                marginLeft: '5px',
                alignSelf: 'center',
              }}
            />
          </div>
        );
      });

      return <div className="flex flex-col gap-2">{filePreviews}</div>;
    };

    return (
      <>
        {files.length > 0 && <div className="mb-4">{renderPreview()}</div>}

        <div
          {...getRootProps({ style: { width: '100%' } })}
          className="flex items-center justify-end w-full"
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <CloudUpload className="w-10 h-10 mb-3 text-gray-400" />
              <p className="mb-2 text-sm text-gray-500">
                {t('CLICK_TO_UPLOAD_OR_DRAG_AND_DROP')}
              </p>
              {fileTypesLabel && (
                <p className="text-xs text-gray-500">{fileTypesLabel}</p>
              )}
            </div>
          </div>
        </div>
      </>
    );
  },
);

FileUploader.displayName = 'FileUploader';

FileUploader.propTypes = {
  onChange: PropTypes.func,
  fileTypes: PropTypes.string,
  fileTypesLabel: PropTypes.string,
  multiple: PropTypes.bool,
};
